import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  SparklesIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowLeftIcon,
  AcademicCapIcon,
  ArrowPathIcon,
  FlagIcon,
  GlobeAltIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

const Quizzes: React.FC = () => {
  const pageUrl = 'https://upmindy.com/docs/quizzes';
  const pageTitle = 'Quizzes Interativos | Upmindy';
  const pageDescription = 'Aprenda a usar o sistema de quizzes do Upmindy com questões de múltipla escolha, sistema de revisão adaptativa e estatísticas detalhadas.';

  return (
    <div className="prose dark:prose-invert max-w-none">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <Link 
        to="/docs" 
        className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6 no-underline"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Voltar para Documentação
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Quizzes Interativos
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
          Teste e reforce seu conhecimento com quizzes de múltipla escolha que se adaptam ao seu desempenho, 
          oferecendo feedback imediato e sistema inteligente de revisão.
        </p>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-8 rounded-2xl border border-purple-100 dark:border-gray-700 shadow-sm mb-8 not-prose">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
            <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
          </div>
          <h2 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
            Por que usar Quizzes?
          </h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
          Quizzes são uma forma comprovada de aprendizagem ativa. Diferente dos flashcards que focam em memorização, 
          os quizzes testam sua compreensão, aplicação de conceitos e capacidade de reconhecer a resposta correta 
          entre alternativas.
        </p>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Como Funciona</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Fluxo de um Quiz</h3>
        <ol className="space-y-4 text-gray-600 dark:text-gray-300">
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-purple-500 dark:bg-purple-400 text-white rounded-full flex items-center justify-center font-bold mr-3">1</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Escolha um quiz:</strong> Selecione um quiz existente ou crie um novo baseado em seus decks ou manualmente.
            </div>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-purple-500 dark:bg-purple-400 text-white rounded-full flex items-center justify-center font-bold mr-3">2</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Responda as questões:</strong> Leia cada pergunta e escolha a alternativa correta entre as opções apresentadas.
            </div>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-purple-500 dark:bg-purple-400 text-white rounded-full flex items-center justify-center font-bold mr-3">3</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Feedback imediato:</strong> Ao responder, você vê instantaneamente se acertou ou errou, com explicação quando disponível.
            </div>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-purple-500 dark:bg-purple-400 text-white rounded-full flex items-center justify-center font-bold mr-3">4</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Revisão inteligente:</strong> Questões erradas são marcadas para revisão futura, garantindo que você domine todo o conteúdo.
            </div>
          </li>
        </ol>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Tipos de Quizzes</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 not-prose">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-6 rounded-xl border border-blue-100 dark:border-gray-700">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <AcademicCapIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="ml-3 text-lg font-bold text-gray-900 dark:text-gray-100">
              Baseado em Deck
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Gere automaticamente questões a partir de um deck de flashcards existente. 
            O sistema cria alternativas e questões baseadas nos seus cartões.
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 p-6 rounded-xl border border-green-100 dark:border-gray-700">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="ml-3 text-lg font-bold text-gray-900 dark:text-gray-100">
              Personalizado
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Crie questões manualmente com suas próprias perguntas, alternativas e explicações. 
            Ideal para conteúdo específico e personalizado.
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 p-6 rounded-xl border border-purple-100 dark:border-gray-700">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="ml-3 text-lg font-bold text-gray-900 dark:text-gray-100">
              Gerado por IA
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Deixe a inteligência artificial criar questões para você. Forneça um tema, 
            nível de dificuldade e número de questões, e a IA faz o resto.
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Sistema de Pontuação</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Cada quiz rastreia seu desempenho através de métricas detalhadas:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3">
              <TrophyIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Pontuação Final</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Porcentagem de acertos calculada ao final do quiz
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
              <ChartBarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Histórico de Tentativas</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Acompanhe sua evolução ao longo das tentativas
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Questões para Revisar</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Questões erradas são marcadas para estudo adicional
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg mr-3">
              <ChartBarIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Tempo de Resposta</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Veja quanto tempo levou para completar o quiz
              </p>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Recursos Avançados</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <ul className="space-y-4 text-gray-600 dark:text-gray-300">
          <li className="flex items-start">
            <ArrowPathIcon className="h-6 w-6 mr-3 flex-shrink-0 text-blue-600 dark:text-blue-400" />
            <div>
              <strong className="text-gray-900 dark:text-white">Modo de Revisão:</strong>
              <p className="text-sm">Revise apenas as questões que você errou anteriormente para focar no que precisa melhorar.</p>
            </div>
          </li>
          <li className="flex items-start">
            <FlagIcon className="h-6 w-6 mr-3 flex-shrink-0 text-purple-600 dark:text-purple-400" />
            <div>
              <strong className="text-gray-900 dark:text-white">Embaralhamento:</strong>
              <p className="text-sm">Questões e alternativas podem ser embaralhadas para evitar memorização da ordem.</p>
            </div>
          </li>
          <li className="flex items-start">
            <ChartBarIcon className="h-6 w-6 mr-3 flex-shrink-0 text-green-600 dark:text-green-400" />
            <div>
              <strong className="text-gray-900 dark:text-white">Estatísticas Detalhadas:</strong>
              <p className="text-sm">Veja seu desempenho por categoria, dificuldade e ao longo do tempo.</p>
            </div>
          </li>
          <li className="flex items-start">
            <GlobeAltIcon className="h-6 w-6 mr-3 flex-shrink-0 text-indigo-600 dark:text-indigo-400" />
            <div>
              <strong className="text-gray-900 dark:text-white">Quizzes Públicos:</strong>
              <p className="text-sm">Compartilhe seus quizzes com outros usuários ou copie quizzes públicos criados pela comunidade.</p>
            </div>
          </li>
        </ul>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Melhores Práticas</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <ul className="space-y-3 text-gray-600 dark:text-gray-300">
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Use após estudar:</strong> Quizzes são mais eficazes depois de você já ter estudado o material com flashcards.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Não decore as respostas:</strong> Tente entender o conceito por trás de cada questão, não apenas memorizar a resposta correta.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Revise os erros:</strong> Use o modo de revisão para focar nas questões que você errou.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Varie a dificuldade:</strong> Combine questões fáceis, médias e difíceis para desafiar seu conhecimento.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Faça regularmente:</strong> Quizzes periódicos ajudam a identificar lacunas de conhecimento antes de provas ou avaliações.</span>
          </li>
        </ul>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Diferença: Quiz vs Flashcard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 not-prose">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
          <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-3">Flashcards</h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li>• Foco em memorização e recall ativo</li>
            <li>• Repetição espaçada automatizada</li>
            <li>• Melhor para vocabulário e fatos</li>
            <li>• Você produz a resposta</li>
          </ul>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-xl border border-purple-200 dark:border-purple-800">
          <h3 className="font-bold text-purple-900 dark:text-purple-300 mb-3">Quizzes</h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li>• Foco em compreensão e reconhecimento</li>
            <li>• Sistema de revisão baseado em performance</li>
            <li>• Melhor para conceitos e aplicações</li>
            <li>• Você reconhece a resposta</li>
          </ul>
        </div>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800 not-prose">
        <div className="flex items-start">
          <LightBulbIcon className="h-8 w-8 mr-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Dica de Ouro
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Combine flashcards e quizzes para aprendizado máximo! Use flashcards para memorização inicial 
              e revisão espaçada, depois teste sua compreensão com quizzes. Esta abordagem dupla melhora 
              significativamente a retenção e o entendimento profundo do conteúdo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quizzes;
